import { strToU8, zipSync } from 'fflate';
import type { Mesh } from './box';

/** The P1S bed is 256 × 256 mm; the box stands on its centre. */
const BED = 256;

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/>
</Types>
`;

const RELS = `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/>
</Relationships>
`;

/**
 * A core-spec 3MF: one named object and a build item that centres it on the
 * bed. No Bambu metadata — Bambu Studio fills in printer and filament from
 * whatever the user has selected. `name` comes from `boxName`, which is
 * digits, letters, dots and dashes only, so it needs no XML escaping.
 */
function modelXml(name: string, mesh: Mesh): string {
  const xs = mesh.vertices.map(([x]) => x);
  const ys = mesh.vertices.map(([, y]) => y);
  const tx = BED / 2 - Math.max(...xs) / 2;
  const ty = BED / 2 - Math.max(...ys) / 2;
  const vertices = mesh.vertices
    .map(([x, y, z]) => `<vertex x="${x}" y="${y}" z="${z}"/>`)
    .join('\n          ');
  const triangles = mesh.triangles
    .map(([a, b, c]) => `<triangle v1="${a}" v2="${b}" v3="${c}"/>`)
    .join('\n          ');
  return `<?xml version="1.0" encoding="UTF-8"?>
<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02">
  <metadata name="Title">${name}</metadata>
  <resources>
    <object id="1" type="model" name="${name}">
      <mesh>
        <vertices>
          ${vertices}
        </vertices>
        <triangles>
          ${triangles}
        </triangles>
      </mesh>
    </object>
  </resources>
  <build>
    <item objectid="1" transform="1 0 0 0 1 0 0 0 1 ${tx} ${ty} 0"/>
  </build>
</model>
`;
}

/** The whole .3mf file: an OPC package (a ZIP) around the model. */
export function buildThreeMf(name: string, mesh: Mesh): Uint8Array {
  return zipSync({
    '[Content_Types].xml': strToU8(CONTENT_TYPES),
    '_rels/.rels': strToU8(RELS),
    '3D/3dmodel.model': strToU8(modelXml(name, mesh)),
  });
}
